import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  Camera,
  FileText,
  CheckCircle,
  Loader2,
  X,
} from "lucide-react";
import api from "../services/api";
import toast from "react-hot-toast";
import HelpButton from "../components/HelpButton";
import { Sentry } from "../services/sentry";
import { uploadFilesViaS3 } from "../utils/uploadToS3";

function DocumentUploadDetail() {
  const { applicationId, queryId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [application, setApplication] = useState(null);
  const [documentCategories, setDocumentCategories] = useState(null);

  // Form states
  const [docCategory, setDocCategory] = useState("");
  const [docTypeSelect, setDocTypeSelect] = useState("");
  const [docTypeOther, setDocTypeOther] = useState("");
  const [customerType, setCustomerType] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [profileOptions, setProfileOptions] = useState([]);
  const [remarks, setRemarks] = useState("");
  // const [imageMode, setImageMode] = useState("UPLOAD");
  const [attachments, setAttachments] = useState([]);
  // const [cameraActive, setCameraActive] = useState(false);
  // const [capturedImage, setCapturedImage] = useState(null);
  // const [stream, setStream] = useState(null);

  // Hardcoded fallback document types
  const hardcodedDoctypes = {
    ADDITIONAL_ADDRESS_PROOF: [
      "BANK_STATEMENT_HAVING_CUSTOMER_CURRENT_RESIDENTIAL_ADDRESS",
      "ELECTRICITY_BILL",
      "GAS_BILL",
      "MOBILE_BILL",
      "PROPERTY_OR_MUNICIPAL_TAX_RECEIPT",
      "RENT_AGREEMENT",
      "SALES_DEED_OR_SALE_AGREEMENT",
      "TELEPHONE",
      "WATER_BILL",
    ],
    ASSET_DOCUMENTS: [
      "INVOICE",
      "MARGIN_MONEY_RECEIPT",
      "Signed Purchase Order",
      "VEHICLE_INSURANCE",
    ],
    COMMUNICATION_ADDRESS_PROOF: [
      "BANK_STATEMENT",
      "ELECTRICITY_BILL",
      "GAS_BILL",
      "RENT_AGREEMENT",
      "SALE_AGREEMENT",
      "TELEPHONE_BILL",
    ],
    DECLARATION: ["CUSTOMER_DECLARATION"],
    DISBURSAL_DOCUMENTS: [
      "MMR/INVOICE",
      "RTO_FORM",
      "SIGNED_APPLICATION_FORM",
      "SIGNED_LOAN_AGREEMENT",
      "SIGNED_SANCTION_LETTER",
    ],
    DOB_PROOF: ["PAN"],
    KYC: [
      "AADHAAR_BACK",
      "AADHAAR_FRONT",
      "DRIVING_LICENSE",
      "PASSPORT_BACK",
      "PASSPORT_FRONT",
      "VOTERS_ID_BACK",
      "VOTERS_ID_FRONT",
    ],
    KYC_FROM_SOURCE: ["AADHAAR_XML", "CKYC", "DIGILOCKER"],
    "KYC_POA_(CA-NON-OVD)(OSV-DONE)": [
      "BANK_STATEMENT_HAVING_CUSTOMER_CURRENT_RESIDENTIAL_ADDRESS",
      "ELECTRICITY_BILL",
      "GAS_BILL",
      "MOBILE_BILL",
      "PROPERTY_OR_MUNICIPAL_TAX_RECEIPT",
      "RENT_AGREEMENT",
      "SALES_DEED_OR_SALE_AGREEMENT",
      "TELEPHONE",
      "WATER_BILL",
    ],
    "KYC_POA_(CA-NON-OVD)(WITHOUT-OSV)": [
      "BANK_STATEMENT_HAVING_CUSTOMER_CURRENT_RESIDENTIAL_ADDRESS",
      "ELECTRICITY_BILL",
      "GAS_BILL",
      "MOBILE_BILL",
      "PROPERTY_OR_MUNICIPAL_TAX_RECEIPT",
      "RENT_AGREEMENT",
      "SALES_DEED_OR_SALE_AGREEMENT",
      "TELEPHONE",
      "WATER_BILL",
    ],
    "KYC_POA_(CA-OVD)(OSV-DONE)": [
      "AADHAR_BACK",
      "AADHAR_FRONT",
      "DRIVING_LICENSE",
      "PASSPORT",
      "VOTERS_ID",
    ],
    "KYC_POA_(CA-OVD)(WITHOUT-OSV)": [
      "AADHAR_BACK",
      "AADHAR_FRONT",
      "DRIVING_LICENSE",
      "PASSPORT",
      "VOTERS_ID",
    ],
    "KYC_POA_(PA-OVD)(OSV-DONE)": [
      "AADHAR_BACK",
      "AADHAR_FRONT",
      "DRIVING_LICENSE",
      "PASSPORT",
      "VOTERS_ID",
    ],
    "KYC_POA_(PA-OVD)(WITHOUT-OSV)": [
      "AADHAR_BACK",
      "AADHAR_FRONT",
      "DRIVING_LICENSE",
      "PASSPORT",
      "VOTERS_ID",
    ],
    LOAN_DOCUMENTS: [
      "CAF_WITH_PHOTO",
      "Deed of Guarantee",
      "DEED_OF_GUARANTEE",
      "DEED_OF_HYPOTHECATION",
      "FORM60",
      "FORM601",
      "KFS",
      "LOAN_AGREEMENT",
      "SIGNED_FORM 60",
      "VERNACULAR_CAF_WITH_PHOTO",
      "VERNACULAR_KFS",
      "VERNACULAR_LOAN_AGREEMENT",
    ],
    NACH: ["NACH_FORM", "NACH_FORM_UNSIGNED"],
    PAN: ["FORM60", "FORM607", "FORM609", "PAN"],
    QUERY: ["QUERY"],
    RELATIONSHIP_PROOF: ["PAN"],
    RTO_DOCUMENTS: ["RTO_FORM", "RTO_RECEIPT"],
    SIGNATURE_PROOF: ["PAN"],
    TECHNICAL_ASSESSMENT: [
      "DPR",
      "ELECTRICITY_BILL",
      "OTHER_TECHNICAL_DOCUMENT",
      "PROPERTY_PHOTO",
    ],
  };

  // Get the current document types (from API or fallback)
  const doctypes = documentCategories || hardcodedDoctypes;

  useEffect(() => {
    loadApplication();
    // return () => {
    //   stopCamera();
    // };
  }, [applicationId]);

  useEffect(() => {
    if (docCategory) {
      setDocTypeSelect("");
      setDocTypeOther("");
    }
  }, [docCategory]);

  useEffect(() => {
    if (customerType && application) {
      updateProfileOptions();
    }
  }, [customerType, application]);

  const loadApplication = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/api/mobile/applications/${applicationId}`
      );
      if (response.data.success) {
        setApplication(response.data.data);
        // Only load document categories if application loads successfully
        loadDocumentCategories();
      }
    } catch (error) {
      console.error("Failed to load application:", error);
      toast.error(error.response?.data?.message || "Failed to load application details");
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentCategories = async () => {
    try {
      const response = await api.get("/api/mobile/document-categories");
      if (response.data.success && response.data.data) {
        setDocumentCategories(response.data.data);
        console.log("Document categories loaded from API");
      }
    } catch (error) {
      console.error("Failed to load document categories from API:", error);
      toast.error("Failed to load document categories, using default list");
      // Using hardcoded fallback - no need to set state as it's the default
    }
  };

  const updateProfileOptions = () => {
    if (!application) return;

    let options = {};

    switch (customerType) {
      case "CUSTOMER":
        if (application.customer) {
          options[application.customer.id] = `${
            application.customer.profile_first_name || ""
          } (${application.customer.id})`;
        }
        break;
      case "GUARANTOR":
        if (application.guarantor) {
          options[application.guarantor.id] = `${
            application.guarantor.profile_first_name || ""
          } (${application.guarantor.id})`;
        }
        break;
      case "COAPPLICANT":
        if (application.coapplicants && application.coapplicants.length > 0) {
          application.coapplicants.forEach((co) => {
            if (co.profile) {
              options[co.profile.id] = `${
                co.profile.profile_first_name || ""
              } (${co.profile.id})`;
            }
          });
        }
        break;
    }

    setProfileOptions(options);

    // Auto-select if only one option
    const optionKeys = Object.keys(options);
    if (optionKeys.length === 1) {
      setSelectedProfileId(optionKeys[0]);
    } else {
      setSelectedProfileId("");
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Materialize selected files into in-memory File objects immediately.
    // On Android Chrome the File from <input type=file> can be backed by a
    // content:// URI that the OS invalidates before the XHR flush, producing
    // ERR_NETWORK / status 0 with no further info. Reading into an ArrayBuffer
    // here detaches the data from the OS handle.
    try {
      const materialized = await Promise.all(
        files.map(async (file) => {
          const buf = await file.arrayBuffer();
          return new File([buf], file.name, {
            type: file.type || "application/octet-stream",
            lastModified: file.lastModified,
          });
        })
      );
      setAttachments((prev) => [...prev, ...materialized]);
    } catch (err) {
      console.error("Failed to read selected file(s):", err);
      toast.error("Could not read selected file. Please pick it again.");
      Sentry.captureException(err, {
        tags: { feature: "document_upload", phase: "file_read" },
      });
    } finally {
      // Allow re-selecting the same file after an error
      if (e.target) e.target.value = "";
    }
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  // const startCamera = async () => {
  //   try {
  //     const mediaStream = await navigator.mediaDevices.getUserMedia({
  //       video: { facingMode: "environment" },
  //     });
  //     if (videoRef.current) {
  //       videoRef.current.srcObject = mediaStream;
  //     }
  //     setStream(mediaStream);
  //     setCameraActive(true);
  //   } catch (error) {
  //     console.error("Error accessing camera:", error);
  //     toast.error("Unable to access camera");
  //   }
  // };

  // const stopCamera = () => {
  //   if (stream) {
  //     stream.getTracks().forEach((track) => track.stop());
  //     setStream(null);
  //   }
  //   setCameraActive(false);
  // };

  // const captureImage = () => {
  //   if (videoRef.current && canvasRef.current) {
  //     const canvas = canvasRef.current;
  //     const video = videoRef.current;
  //     canvas.width = video.videoWidth;
  //     canvas.height = video.videoHeight;
  //     const ctx = canvas.getContext("2d");
  //     ctx.drawImage(video, 0, 0);

  //     canvas.toBlob((blob) => {
  //       const file = new File([blob], `capture_${Date.now()}.jpg`, {
  //         type: "image/jpeg",
  //       });
  //       setCapturedImage(file);
  //       stopCamera();
  //     }, "image/jpeg");
  //   }
  // };

  const handleSubmit = async () => {
    // Validation
    if (!docCategory) {
      toast.error("Please select document category");
      return;
    }

    if (docCategory === "OTHER" && !docTypeOther) {
      toast.error("Please enter document type");
      return;
    }

    if (docCategory !== "OTHER" && !docTypeSelect) {
      toast.error("Please select document type");
      return;
    }

    if (!customerType) {
      toast.error("Please select customer type");
      return;
    }

    if (!selectedProfileId) {
      toast.error("Please select profile");
      return;
    }

    if (attachments.length === 0) {
      toast.error("Please upload a file");
      return;
    }

    setUploading(true);
    window.__appBusy = true; // pause any pending SW auto-reload

    // Hoisted so the catch block can include them in the Sentry context
    let maxLoaded = 0;
    let lastTotal = 0;
    let attemptCount = 0;
    let uploadStep = "init";

    try {
      const totalSize = attachments.reduce((s, f) => s + f.size, 0);
      console.log(`Upload size: ${(totalSize / 1024 / 1024).toFixed(2)}MB`);

      const maxSize = 30 * 1024 * 1024; // 30MB hard cap (server allows 40MB post_max_size)
      if (totalSize > maxSize) {
        toast.error(
          `Files too large: ${(totalSize / 1024 / 1024).toFixed(2)}MB. Max: 30MB`
        );
        setUploading(false);
        window.__appBusy = false;
        return;
      }

      const buildCommitPayload = (upload) => ({
        upload_id: upload.upload_id,
        application_id: applicationId,
        profile_id: selectedProfileId,
        doc_category: docCategory,
        doc_type: docCategory === "OTHER" ? docTypeOther : docTypeSelect,
        customer_type: customerType,
        query_id: queryId || null,
        remarks: remarks || null,
        files: upload.files,
      });

      // Log diagnostic info
      console.log("=== UPLOAD DIAGNOSTICS ===");
      console.log("Device:", navigator.userAgent);
      console.log("Network:", navigator.connection?.effectiveType || "unknown");
      console.log("Online:", navigator.onLine);
      console.log("File count:", attachments.length);
      console.log(
        "File details:",
        attachments.map((f) => ({ name: f.name, size: f.size, type: f.type }))
      );
      console.log("API Base URL:", import.meta.env.VITE_API_BASE_URL || "default");
      console.log("========================");

      let lastLoggedPct = 0;
      attemptCount = 1;

      uploadStep = "presign+s3_put";
      const upload = await uploadFilesViaS3(
        "/api/mobile/upload-document/presign",
        attachments,
        { application_id: applicationId },
        (l, t) => {
          if (l > maxLoaded) maxLoaded = l;
          if (t) lastTotal = t;
          if (t > 0) {
            const pct = Math.round((l / t) * 100);
            if (pct - lastLoggedPct >= 10 || pct === 100) {
              lastLoggedPct = pct;
              console.log(`Upload progress: ${pct}% (${l}/${t})`);
            }
          }
        }
      );

      uploadStep = "commit";
      const response = await api.post(
        "/api/mobile/upload-document/commit",
        buildCommitPayload(upload),
        { timeout: 60000 }
      );

      if (response.data.success) {
        toast.success("Document uploaded successfully");
        navigate(-1);
      }
    } catch (error) {
      console.error("Failed to upload document:", error);
      console.error("Error response:", error.response);
      console.error("Error request:", error.request);
      console.error("Error message:", error.message);
      console.error("Full error object:", JSON.stringify(error, Object.getOwnPropertyNames(error), 2));

      // Report to Sentry with rich context
      Sentry.captureException(error, {
        tags: {
          feature: "document_upload",
          error_code: error.code || "unknown",
          http_status: error.response?.status || "no_response",
        },
        contexts: {
          upload: {
            application_id: applicationId,
            query_id: queryId || null,
            doc_category: docCategory,
            doc_type: docCategory === "OTHER" ? docTypeOther : docTypeSelect,
            customer_type: customerType,
            profile_id: selectedProfileId,
            file_count: attachments.length,
            total_size_mb: (
              attachments.reduce((s, f) => s + f.size, 0) / 1024 / 1024
            ).toFixed(2),
            files: attachments.map((f) => ({
              name: f.name,
              size: f.size,
              type: f.type,
            })),
          },
          network: {
            online: navigator.onLine,
            effective_type: navigator.connection?.effectiveType || "unknown",
            downlink: navigator.connection?.downlink || null,
            rtt: navigator.connection?.rtt || null,
            bytes_uploaded_before_failure: maxLoaded,
            total_bytes: lastTotal,
            upload_complete_pct:
              lastTotal > 0 ? Math.round((maxLoaded / lastTotal) * 100) : null,
            attempts: attemptCount,
            failed_step: uploadStep,
          },
          response: {
            status: error.response?.status || null,
            data: error.response?.data || null,
            headers: error.response?.headers || null,
          },
        },
      });

      // Handle different error response structures
      let errorMessage = "Failed to upload document";

      if (error.response?.data) {
        errorMessage = error.response.data.message || errorMessage;

        if (error.response.data.errors) {
          const validationErrors = Object.values(error.response.data.errors).flat();
          errorMessage += ": " + validationErrors.join(", ");
        }
      } else if (error.request) {
        errorMessage = error.message || "No response from server";
      } else {
        errorMessage = error.message || errorMessage;
      }

      toast.error(errorMessage);
    } finally {
      setUploading(false);
      window.__appBusy = false;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-300 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 transition-colors duration-300 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/80 dark:bg-gray-900/80 border-b border-white/20 dark:border-gray-800 shadow-lg safe-top transition-colors duration-300">
        <div className="px-4 py-2.5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors active:scale-95"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </button>
            <div>
              <h1 className="text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent leading-tight">
                Upload Document
              </h1>
              <p className="text-[10px] text-gray-600 dark:text-gray-400 leading-tight">
                App #{applicationId}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 py-3 pb-20 overflow-y-auto">
        <div className="max-w-2xl mx-auto space-y-3">
          {/* Document Category */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-md border border-gray-100 dark:border-gray-700">
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Document Category *
            </label>
            <select
              value={docCategory}
              onChange={(e) => setDocCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            >
              <option value="">Select Category</option>
              {Object.keys(doctypes).map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Document Type */}
          {docCategory && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-md border border-gray-100 dark:border-gray-700">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Document Type *
              </label>
              {docCategory === "OTHER" ? (
                <input
                  type="text"
                  value={docTypeOther}
                  onChange={(e) => setDocTypeOther(e.target.value)}
                  placeholder="Enter document type"
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              ) : (
                <select
                  value={docTypeSelect}
                  onChange={(e) => setDocTypeSelect(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                >
                  <option value="">Select Type</option>
                  {doctypes[docCategory]?.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Customer Type */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-md border border-gray-100 dark:border-gray-700">
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Customer Type *
            </label>
            <select
              value={customerType}
              onChange={(e) => setCustomerType(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            >
              <option value="">Select Customer Type</option>
              <option value="CUSTOMER">Customer</option>
              <option value="COAPPLICANT">Co-Applicant</option>
              <option value="GUARANTOR">Guarantor</option>
            </select>
          </div>

          {/* Profile Selection */}
          {customerType && Object.keys(profileOptions).length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-md border border-gray-100 dark:border-gray-700">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Select Profile *
              </label>
              <select
                value={selectedProfileId}
                onChange={(e) => setSelectedProfileId(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              >
                <option value="">Select Profile</option>
                {Object.entries(profileOptions).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Upload Method - Commented out as mobile native file picker handles this */}
          {/* <div className="bg-white rounded-2xl p-3 shadow-md border border-gray-100">
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Upload Method
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setImageMode("UPLOAD");
                  stopCamera();
                  setCapturedImage(null);
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                  imageMode === "UPLOAD"
                    ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Upload className="w-4 h-4" />
                Upload
              </button>
              <button
                onClick={() => {
                  setImageMode("TAKE");
                  setAttachments([]);
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
                  imageMode === "TAKE"
                    ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Camera className="w-4 h-4" />
                Camera
              </button>
            </div>
          </div> */}

          {/* Remarks */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-md border border-gray-100 dark:border-gray-700">
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Remarks (Optional)
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Add any additional notes or remarks..."
              rows={2}
              className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
            />
          </div>

          {/* File Upload */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-3 shadow-md border border-gray-100 dark:border-gray-700">
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Select File *
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              accept="image/*,.pdf"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full px-3 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-indigo-500 transition-all flex flex-col items-center gap-1.5"
            >
              <Upload className="w-6 h-6 text-gray-400" />
              <span className="text-xs text-gray-600 dark:text-gray-400">Click to upload</span>
            </button>

            {attachments.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl"
                  >
                    <FileText className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                    <span className="flex-1 text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                      {file.name}
                    </span>
                    <button
                      onClick={() => removeAttachment(index)}
                      className="p-0.5 hover:bg-white dark:hover:bg-gray-700 rounded-full transition-colors flex-shrink-0"
                    >
                      <X className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Camera Capture - Commented out as mobile native file picker handles this */}
          {/* {imageMode === "TAKE" && (
            <div className="bg-white rounded-2xl p-3 shadow-md border border-gray-100">
              <label className="block text-xs font-semibold text-gray-700 mb-2">
                Capture Image *
              </label>

              {!cameraActive && !capturedImage && (
                <button
                  onClick={startCamera}
                  className="w-full px-3 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl text-sm font-semibold hover:shadow-md transition-all active:scale-95 flex items-center justify-center gap-1.5"
                >
                  <Camera className="w-4 h-4" />
                  Start Camera
                </button>
              )}

              {cameraActive && (
                <div className="space-y-2">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full rounded-xl"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={captureImage}
                      className="flex-1 px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl text-sm font-semibold hover:shadow-md transition-all active:scale-95"
                    >
                      Capture
                    </button>
                    <button
                      onClick={stopCamera}
                      className="flex-1 px-3 py-2 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-xl text-sm font-semibold hover:shadow-md transition-all active:scale-95"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {capturedImage && (
                <div className="space-y-2">
                  <div className="relative">
                    <img
                      src={URL.createObjectURL(capturedImage)}
                      alt="Captured"
                      className="w-full rounded-xl"
                    />
                    <button
                      onClick={() => {
                        setCapturedImage(null);
                        startCamera();
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5 text-green-600 bg-green-50 p-2 rounded-xl">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-xs font-semibold">
                      Image captured
                    </span>
                  </div>
                </div>
              )}

              <canvas ref={canvasRef} className="hidden" />
            </div>
          )} */}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={uploading}
            className="w-full px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-2xl font-bold text-base shadow-md hover:shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Upload Document
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Help Button */}
      <HelpButton />
    </div>
  );
}

export default DocumentUploadDetail;
